import { supabase } from "@cl/supabase";
import type { EmailPref, NotificationType } from "@u/notifications";

export interface AppNotification {
  id: string;
  user_id: string;
  type: NotificationType;
  source: "tour" | "system";
  entity_id: string | null;
  tour_id: string | null;
  meta: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export interface StoredPreference {
  type: NotificationType;
  in_app: boolean;
  email: EmailPref;
}

export async function fetchNotifications(limit = 30): Promise<AppNotification[]> {
  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as AppNotification[];
}

export async function fetchUnreadCount(): Promise<number> {
  const { count, error } = await supabase
    .from("notifications")
    .select("id", { count: "exact", head: true })
    .is("read_at", null);
  if (error) throw error;
  return count ?? 0;
}

export async function markRead(id: string): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function markAllRead(): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .is("read_at", null);
  if (error) throw error;
}

export async function fetchPreferences(): Promise<StoredPreference[]> {
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("type, in_app, email");
  if (error) throw error;
  return (data ?? []) as StoredPreference[];
}

export async function upsertPreference(
  userId: string,
  pref: StoredPreference,
): Promise<void> {
  const { error } = await supabase
    .from("notification_preferences")
    .upsert({ user_id: userId, ...pref }, { onConflict: "user_id,type" });
  if (error) throw error;
}
